import React, { Fragment, useState, useEffect } from 'react';
import {
  Stack,
  Grid,
  Typography,
  Box,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { getVehicleUpgradeOptions } from '../../services/vehicles';
import VehiclePricingTable from './VehiclePricingTable';
import { countryMapping } from '../../utils/constants';
import { usePermissions } from '../../hooks/usePermissions';
import { Typography as Typo } from '../../components';

const AssignmentActionModal = (props: any) => {
  const { componentChange, inventoryVehicle, orderInfo, transactionErrors } = props;
  const { hasPermissions } = usePermissions();

  const useHarcodedValues = {
    wheelOptions: false,
    additionalOptions: true,
  };
  const hardCodedSwUpgradesValues = [
    {
      name: 'Dream Drive',
      selected: false,
      categories: [
        {
          code: 'ZL_ADA',
          name: 'DreamDrive Pro',
          description:
            'Our advanced driver assistance system features one of the most comprehensive sensor suites available and will only get better with software updates over time.',
          price: 9000,
          selected: true,
        },
      ],
      price: 0,
    },
    {
      name: 'Surreal Sound',
      selected: false,
      categories: [
        {
          code: 'ZL_PAP',
          name: 'Surreal Sound Pro',
          description: 'Premium Immersive 21-Speaker 3D Music & Spatial Audio Surround System',
          price: 2900,
          selected: true,
        },
      ],
      price: 0,
    },
  ];

  const [vehiclePricing, setVehiclePricing] = useState({
    wheelSwapFee: 0,
    additionalUpgradeFee: 0,
    inventoryVehiclePrice: orderInfo.countryCode
      ? inventoryVehicle?.vehiclePrice?.filter((a: any) => a.locale === countryMapping[orderInfo.countryCode])[0]
          ?.totalPrice
      : inventoryVehicle?.vehiclePrice[0]?.totalPrice,
    totalPrice: orderInfo.countryCode
      ? inventoryVehicle?.vehiclePrice?.filter((a: any) => a.locale === countryMapping[orderInfo.countryCode])[0]
          ?.totalPrice
      : inventoryVehicle?.vehiclePrice[0]?.totalPrice,
  });
  const [wheelOptions, setWheelOptions] = useState<any[]>([]);
  const [invCurrency, setInvCurrency] = useState(
    orderInfo.countryCode
      ? inventoryVehicle?.vehiclePrice?.filter((a: any) => a.locale === countryMapping[orderInfo.countryCode])[0]
          ?.currency
      : inventoryVehicle?.vehiclePrice[0]?.currency
  );
  // Note: This is hard-coded for the time being as the data from CPQ API is not valid
  const [additionalOptions, setAdditionalOptions] = useState<any[]>([]);

  const wheelSwapOptions = (resp: any) => {
    let wheelSwap: any = [];
    if (hasPermissions(['ACCESS_WHEEL_SWAP_OPTIONS'])) {
      wheelSwap = resp?.wheelOptions?.installed
        .map((wheel: any) => {
          wheel.name = `${wheel.name} (Included)`;
          wheel.selected = true;
          wheel.priceDiff = 0;
          return wheel;
        })
        .concat(
          resp?.wheelOptions.available_upgrades.map((wheel: any) => {
            wheel.selected = false;
            wheel.priceDiff = wheel.price - resp.wheelOptions.installed[0].price;
            return wheel;
          })
        );
    } else {
      wheelSwap = resp?.wheelOptions.installed.map((wheel: any) => {
        wheel.name = `${wheel.name} (Included)`;
        wheel.selected = true;
        wheel.priceDiff = 0;
        return wheel;
      });
    }
    return wheelSwap;
  };

  useEffect(() => {
    getVehicleUpgradeOptions(inventoryVehicle?.batchNumber)
      .then((resp) => {
        const locale = orderInfo.countryCode
          ? countryMapping[orderInfo.countryCode]
          : inventoryVehicle?.vehiclePrice[0]?.locale;
        const installedAndAvailableWheels = wheelSwapOptions && wheelSwapOptions(resp[locale]);
        if (!useHarcodedValues?.wheelOptions) {
          setWheelOptions(installedAndAvailableWheels);
        }
        if (resp[locale]?.upgradeOptions.enable_software_upgrades && useHarcodedValues.additionalOptions) {
          setAdditionalOptions(hardCodedSwUpgradesValues);
        }
        if (resp[locale]?.upgradeOptions.enable_software_upgrades && !useHarcodedValues.additionalOptions) {
          const installedAndAvailableUpgrades = resp[locale]?.upgradeOptions.installed.concat(
            resp[locale]?.upgradeOptions.available_upgrades
          );
          setAdditionalOptions((prevState) => [
            {
              name: 'Dream Drive',
              selected: installedAndAvailableUpgrades.length > 0,
              categories: installedAndAvailableUpgrades
                .filter((item: any) => item.name.toLowerCase().includes('dreamdrive'))
                .map((item: any) => {
                  // eslint-disable-next-line
                  item.price === 0 ? (item.selected = true) : (item.selected = false);
                  return item;
                }),
              price: 0,
            },
            {
              name: 'Surreal Sound',
              selected: installedAndAvailableUpgrades.length > 0,
              categories: installedAndAvailableUpgrades
                .filter((item: any) => item.name.toLowerCase().includes('surreal'))
                .map((item: any) => {
                  // eslint-disable-next-line
                  item.price === 0 ? (item.selected = true) : (item.selected = false);
                  return item;
                }),
              price: 0,
            },
          ]);
        }
      })
      .catch((error) => {
        console.log(error);
      });
    // resetting parent state to null
    componentChange();
  }, [inventoryVehicle]);

  const wheelChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const updatedWheelOptions = wheelOptions.map((option) =>
      option.code === key ? { ...option, selected: true } : { ...option, selected: false }
    );
    setWheelOptions(updatedWheelOptions);
    const updateWheelOption = updatedWheelOptions.find((item) => item.code === key);
    const updatedWheelSwapPrice = updateWheelOption.priceDiff;
    setVehiclePricing((prevState: any) => {
      return {
        ...prevState,
        wheelSwapFee: updatedWheelSwapPrice,
        additionalUpgradeFee: prevState.additionalUpgradeFee - prevState.wheelSwapFee + updatedWheelSwapPrice,
        inventoryVehiclePrice: prevState.inventoryVehiclePrice,
        totalPrice:
          prevState.inventoryVehiclePrice +
          (prevState.additionalUpgradeFee - prevState.wheelSwapFee + updatedWheelSwapPrice),
      };
    });
    if (!updateWheelOption.name.includes('Included')) {
      componentChange('wheelOptions', updateWheelOption);
    } else {
      componentChange('wheelOptions', null);
    }
  };
  const addnOptnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedAddnOptns = additionalOptions.map((option) =>
      option.name === event.target.value ? { ...option, selected: !option.selected } : option
    );
    setAdditionalOptions(updatedAddnOptns);
    updatedAddnOptns.forEach((option) => {
      if (option.name === event.target.value) {
        if (option.selected === true) {
          const priceToAdd = option.categories.find((catg: any) => catg.selected === true).price;
          setVehiclePricing((prevState: any) => {
            return {
              ...prevState,
              wheelSwapFee: prevState.wheelSwapFee,
              additionalUpgradeFee: prevState.additionalUpgradeFee + priceToAdd,
              inventoryVehiclePrice: prevState.inventoryVehiclePrice,
              totalPrice: prevState.totalPrice + priceToAdd,
            };
          });
        }
        if (option.selected === false) {
          const priceToRemove = option.categories.find((catg: any) => catg.selected === true).price;
          setVehiclePricing((prevState: any) => {
            return {
              ...prevState,
              wheelSwapFee: prevState.wheelSwapFee,
              additionalUpgradeFee: prevState.additionalUpgradeFee - priceToRemove,
              inventoryVehiclePrice: prevState.inventoryVehiclePrice,
              totalPrice: prevState.totalPrice - priceToRemove,
            };
          });
        }
      }
    });
    const selectedOptions = updatedAddnOptns.reduce((acc, curr) => {
      if (curr.selected === true) {
        const selectedCatg = curr.categories.find((catg: any) => catg.selected === true);
        if (selectedCatg) acc.push(selectedCatg);
      }
      return acc;
    }, []);

    componentChange('upgradeOptions', selectedOptions);
  };

  const addnSubOptnChangeHandler = (event: any, name: string) => {
    const updatedSubOptions = additionalOptions.map((option) =>
      option.name === name
        ? {
            ...option,
            selected: true,
            categories: option.categories.map((catg: any) =>
              catg.code === event.target.value ? { ...catg, selected: true } : { ...catg, selected: false }
            ),
          }
        : option
    );
    setAdditionalOptions(updatedSubOptions);

    const updatedUpgradeFee = updatedSubOptions.reduce((accumulator, currentOptn) => {
      if (currentOptn.selected === true) {
        const selectedOptnPrice = currentOptn.categories.find((catg: any) => catg.selected === true).price;
        accumulator += selectedOptnPrice;
      }
      return accumulator;
    }, 0);
    setVehiclePricing((prevState: any) => {
      return {
        ...prevState,
        wheelSwapFee: prevState.wheelSwapFee,
        additionalUpgradeFee: updatedUpgradeFee,
        inventoryVehiclePrice: prevState.inventoryVehiclePrice,
        totalPrice: prevState.inventoryVehiclePrice + prevState.wheelSwapFee + updatedUpgradeFee,
      };
    });
  };
  const currencyFormatter = (value: number, currency: string) => {
    if (Number.isNaN(value) || value === undefined || value == null) {
      return '-';
    }
    if (currency && currency.length > 0) {
      try {
        const formattedValue = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
        return `${formattedValue}`;
      } catch (error) {
        return `${Math.abs(+value).toFixed(2)}`;
      }
    }
    return `${Math.abs(+value).toFixed(2)}`;
  };
  return (
    <>
      {transactionErrors && (
        <Box sx={{ display: 'flex', bgcolor: '#FFDBD7', padding: '7px 10px', borderRadius: '4px' }}>
          <Box>
            <Icon icon="ri:information-line" color="#E04732" width="16px" height="16px" style={{ paddingTop: '2px' }} />
          </Box>
          <Typo weight={400} size={12} color="#E04732" marginLeft={1} lineHeight={1.7}>
            There is an issue processing the release. This request can be found under{' '}
            <span style={{ fontWeight: 'bold' }}>Assignment - Pending Requests</span> tab to retry.
          </Typo>
        </Box>
      )}
      <Box mb={3}>
        <Box display="flex" alignItems="left" mb={1}>
          <Typography variant="h6" component="h2" fontWeight="bold">
            Request assignment or hold vehicle
          </Typography>
        </Box>
        <Typography variant="caption">Please fill out the form and submit.</Typography>
      </Box>
      <Box display="flex" alignItems="left" mb={0}>
        <Stack direction="row" spacing={3} mb={2}>
          <Stack direction="column" spacing={0}>
            {orderInfo?.orderNumber && (
              <>
                <Typography fontSize="14px" lineHeight="18.2px" color="#717171">
                  Order Number
                </Typography>
                <Typography variant="caption" fontWeight="500" fontSize="1rem" lineHeight="19.2px">
                  {orderInfo?.orderNumber}
                </Typography>
              </>
            )}
          </Stack>
          <Stack direction="column" spacing={0}>
            <Typography fontSize="14px" lineHeight="18.2px" color="#717171">
              Customer Name
            </Typography>
            <Typography variant="caption" fontWeight="500" fontSize="1rem" lineHeight="19.2px">
              {orderInfo.customerName ? orderInfo.customerName : `${orderInfo.firstName} ${orderInfo.lastName}`}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        {/* Left section */}
        <Grid item xs={6} border="1px solid grey" borderColor="#EDEDED" borderRadius="4px" pb={10} mb={3}>
          <Stack pt={8} direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Box>
              <img alt="Inventory Vehicle" src={inventoryVehicle?.imageUrl} height="200px" />
            </Box>
            <Stack direction="column" justifyContent="center" alignItems="flex-start" spacing={1} pr={3}>
              <Typography variant="h6" fontWeight="Bold" align="left">
                Inventory Vehicle Selected
              </Typography>
              <Typography variant="caption" fontWeight="Bold" fontSize="1rem">
                {inventoryVehicle?.year}, {inventoryVehicle?.model}
              </Typography>
              <Typography variant="caption" fontSize="1rem">
                {inventoryVehicle?.exteriorColor}, {inventoryVehicle?.interiorColor}, {inventoryVehicle?.roof},{' '}
                {inventoryVehicle?.wheels}
                {'\n'}Currently in {inventoryVehicle?.currentCity}, {inventoryVehicle?.currentState}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Typography variant="subtitle2" fontSize="1rem" fontWeight="Bold">
                  Price
                </Typography>
                <Typography variant="subtitle2" fontSize="1rem" fontWeight="Bold">
                  {`${currencyFormatter(vehiclePricing.inventoryVehiclePrice, invCurrency)}`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Grid>

        {/* Right Section */}
        <Grid item xs={6} mb={3}>
          <Box mb={3}>
            {wheelOptions?.length > 0 && (
              <>
                <Typography variant="caption" fontWeight="500" fontSize="1rem">
                  Wheel Swap
                </Typography>

                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  onChange={(event, key) => wheelChangeHandler(event, key)}
                  sx={{
                    gap: 0.25,
                    ' .MuiTypography-root': {
                      fontSize: 14,
                    },
                    ' svg': {
                      color: (theme) => theme.palette.common.black,
                    },
                  }}
                >
                  {wheelOptions.map((item) => (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} pr={2}>
                      <FormControlLabel
                        key={item.code}
                        value={item.code}
                        control={<Radio checked={item.selected} />}
                        label={item.name}
                      />
                      <Typography variant="body1" align="justify" pt={1}>
                        {item.priceDiff < 0
                          ? `${currencyFormatter(item.priceDiff, invCurrency)}`
                          : `${currencyFormatter(item.priceDiff, invCurrency)}`}
                      </Typography>
                    </Stack>
                  ))}
                </RadioGroup>
              </>
            )}
          </Box>
          <Box mb={3}>
            {/* <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} pt={1} pl={3} pr={2}>
            <Radio name="SOme" checked />
            <p>Lorem Ipsum 2</p>
          </Stack> */}
            {additionalOptions.length > 0 && (
              <>
                <Typography variant="caption" fontWeight="Bold" fontSize="1rem">
                  Add Options
                </Typography>
                <FormGroup
                  sx={{
                    gap: 0.25,
                    ' .MuiTypography-root': {
                      fontSize: 14,
                    },
                    ' svg': {
                      color: (theme) => theme.palette.common.black,
                    },
                  }}
                >
                  {additionalOptions.map((item) =>
                    item.categories && item.categories.length > 0 ? (
                      <Stack pl={3}>
                        <FormControlLabel
                          control={<Checkbox checked={item.selected} />}
                          key={item.name}
                          label={item.name}
                          value={item.name}
                          onChange={(event: any) => addnOptnChangeHandler(event)}
                        />
                        <RadioGroup
                          aria-labelledby="demo-controlled-radio-buttons-group"
                          name="controlled-radio-buttons-group"
                          onChange={(event) => addnSubOptnChangeHandler(event, item.name)}
                          sx={{
                            gap: 0.25,
                            ' .MuiTypography-root': {
                              fontSize: 14,
                            },
                            ' svg': {
                              color: (theme) => theme.palette.common.black,
                            },
                          }}
                        >
                          {item.categories.map((elem: any) => (
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={1}
                              pl={3}
                              pr={2}
                            >
                              <FormControlLabel
                                key={elem.code}
                                value={elem.code}
                                control={<Radio checked={elem.selected} />}
                                label={elem.name}
                              />
                              <Typography variant="body1" align="justify" pt={1}>
                                + {currencyFormatter(elem.price, invCurrency)}
                              </Typography>
                            </Stack>
                          ))}
                        </RadioGroup>
                      </Stack>
                    ) : (
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} pr={2}>
                        <FormControlLabel
                          control={<Checkbox defaultChecked={item.selected} />}
                          label={item.name}
                          onChange={() => null}
                        />
                        <Typography variant="body1" align="justify" pt={1}>
                          + {currencyFormatter(item.price, invCurrency)}
                        </Typography>
                      </Stack>
                    )
                  )}
                </FormGroup>
              </>
            )}
          </Box>

          {/* price change table */}
          <VehiclePricingTable
            vehiclePricing={vehiclePricing}
            currencyFormatter={currencyFormatter}
            locationCurrency={invCurrency}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AssignmentActionModal;
